import { useCallback, useSyncExternalStore } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { getErrorStatus } from '@/lib/api/error-utils'
import {
  isReaderRefused,
  recoverReader,
  refuseReader,
  subscribeReaderRefusals,
} from '@/lib/query/reader-refusals'
import { useQuery, useQueryClient } from '@tanstack/react-query'

const unavailable = new Error('Financial report access is unavailable')

/** Financial data belongs to one immutable workspace/access session. A denied
 * read stays unavailable until a fresh reader succeeds; cache writes cannot
 * restore it. Filter changes never use another selection's placeholder data. */
export function useFinancialReadQuery<T>(options: {
  queryKey: readonly unknown[]
  queryFn: (signal: AbortSignal) => Promise<T>
  enabled?: boolean
  staleTime?: number
}) {
  const scope = useWorkspaceSession()
  const client = useQueryClient()
  const enabled = options.enabled !== false && scope.enabled
  const key = [...options.queryKey, { workspaceSession: scope.key }]
  const query = useQuery<T>({
    queryKey: key,
    enabled,
    staleTime: options.staleTime,
    queryFn: async ({ signal }) => {
      try {
        const value = await scope.run(signal, linked => options.queryFn(linked))
        recoverReader(client, key)
        return value
      } catch (error) {
        if ([401, 403, 404].includes(getErrorStatus(error) ?? 0))
          refuseReader(client, key)
        throw error
      }
    },
  })
  const subscribe = useCallback(
    (listener: () => void) => subscribeReaderRefusals(client, listener),
    [client],
  )
  const refused = useSyncExternalStore(
    subscribe,
    () => isReaderRefused(client, key),
    () => false,
  )
  const error =
    options.enabled === false
      ? null
      : (query.error ?? (!scope.enabled || refused ? unavailable : null))
  return {
    ...query,
    data: enabled && !refused && !query.isError ? query.data : undefined,
    error,
    // Consumers must not mistake an unavailable reader for a successful empty
    // report, even when an older query cache entry still has success status.
    isError: error != null,
    isSuccess: enabled && error == null && query.isSuccess,
  }
}
